import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { api } from '../api/client';
import { endpoints } from '../api/endpoints';
import { clientConfig } from '../config/clientConfig';
import {
  fetchAuthenticatedBlobUrl,
  invalidateMediaBlobCacheFor,
} from '../api/mediaUrl';

const BrandingContext = createContext({
  companyName: clientConfig.fallbackCompanyName,
  logoKey: null,
  logoUrl: null,
  loading: true,
  refreshBranding: async () => {},
});

async function loadBrandingData(previousLogoKey) {
  const { data } = await api.get(endpoints.branding.get);
  if (!data) {
    return {
      companyName: clientConfig.fallbackCompanyName,
      logoKey: null,
      logoUrl: null,
    };
  }

  const logoKey = data.logoKey || null;
  if (previousLogoKey && previousLogoKey !== logoKey) {
    invalidateMediaBlobCacheFor(previousLogoKey);
  }
  if (logoKey) {
    invalidateMediaBlobCacheFor(logoKey);
  }

  let logoUrl = null;
  if (logoKey) {
    try {
      logoUrl = await fetchAuthenticatedBlobUrl(logoKey);
    } catch {
      logoUrl = null;
    }
  }

  return {
    companyName: data.companyName || clientConfig.fallbackCompanyName,
    logoKey,
    logoUrl,
  };
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState({
    companyName: clientConfig.fallbackCompanyName,
    logoKey: null,
    logoUrl: null,
    loading: true,
  });
  const logoKeyRef = useRef(null);

  useEffect(() => {
    logoKeyRef.current = branding.logoKey;
  }, [branding.logoKey]);

  const refreshBranding = useCallback(async () => {
    setBranding((prev) => ({ ...prev, loading: true }));
    try {
      const next = await loadBrandingData(logoKeyRef.current);
      setBranding({ ...next, loading: false });
    } catch {
      setBranding((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadBrandingData(null)
      .then((next) => {
        if (!cancelled) {
          setBranding({ ...next, loading: false });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBranding((prev) => ({ ...prev, loading: false }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <BrandingContext.Provider value={{ ...branding, refreshBranding }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
